
import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Menu, X, LogOut, User, ArrowUpRight } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/i18n/LanguageContext';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { cn } from '@/lib/utils';

const desktopLink = ({ isActive }: { isActive: boolean }) =>
  cn(
    "relative text-sm font-semibold transition-colors hover:text-highlight",
    "after:absolute after:-bottom-1.5 after:left-0 after:h-0.5 after:w-full after:origin-left after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 after:[transition-timing-function:var(--ease-out-quint)] hover:after:scale-x-100",
    isActive ? "text-foreground after:scale-x-100" : "text-muted-foreground",
  );

const mobileLink = ({ isActive }: { isActive: boolean }) =>
  cn(
    "rounded-md px-4 py-3 font-semibold transition-colors hover:bg-secondary hover:text-highlight",
    isActive ? "bg-secondary text-highlight" : "text-foreground",
  );

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userRole, signOut } = useAuth();
  const { t } = useI18n();

  // Close the mobile menu on route change and lock body scroll while it is open.
  useEffect(() => setIsMenuOpen(false), [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);


  const handleDashboardNavigation = () => {
    if (userRole === 'lecturer') navigate('/lecturer/dashboard');
    else if (userRole === 'student') navigate('/student/dashboard');
    else if (userRole === 'admin') navigate('/admin/dashboard');
    setIsMenuOpen(false);
  };

  const roleLabel = userRole === 'lecturer' ? t.nav.lecturer : userRole === 'student' ? t.nav.student : t.nav.admin;

  const navItems = [
    { to: '/', label: t.nav.home, end: true },
    { to: '/news', label: t.nav.news },
    { to: '/admission', label: t.nav.admission },
    { to: '/services', label: t.nav.services },
  ];

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-border/60 bg-background/90 backdrop-blur-xl">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-[72px] items-center justify-between gap-3">
          <Link to="/" className="flex shrink-0 items-center gap-2 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label={t.common.brand}>
            <picture>
              <source
                srcSet="/new-logo.png?w=168&h=40&fit=crop 1x, /new-logo.png?w=336&h=80&fit=crop 2x"
                type="image/png"
              />
              <img
                src="/new-logo.png?w=168&h=40&fit=crop"
                alt="Global IT Academy"
                className="brand-logo h-8 w-auto xs:h-9 sm:h-10"
                width="168"
                height="40"
                loading="eager"
                decoding="async"
              />
            </picture>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden items-center gap-7 lg:flex" aria-label={t.nav.home}>
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} className={desktopLink}>
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="text-sm font-semibold text-muted-foreground transition-colors hover:text-highlight"
            >
              {t.nav.contact}
            </Link>
          </nav>

          {/* Desktop actions */}
          <div className="hidden items-center gap-1.5 lg:flex">
            <LanguageSwitcher />
            <ThemeToggle />
            <div className="mx-1.5 h-6 w-px bg-border/70" aria-hidden="true" />
            {user ? (
              <div className="flex items-center gap-1">
                <Button onClick={handleDashboardNavigation} variant="ghost" size="sm" className="text-primary">
                  <User className="mr-2 h-4 w-4" />
                  {roleLabel}
                </Button>
                <Button onClick={signOut} variant="ghost" size="icon" className="h-10 w-10 text-primary" aria-label={t.nav.signOut} title={t.nav.signOut}>
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button asChild className="h-10 px-5">
                <Link to="/login">
                  {t.nav.login}
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </Button>
            )}
          </div>

          {/* Mobile actions */}
          <div className="flex items-center gap-0.5 lg:hidden">
            <ThemeToggle />
            <Button
              variant="ghost"
              size="icon"
              className="h-11 w-11 text-primary"
              onClick={() => setIsMenuOpen((open) => !open)}
              aria-label={isMenuOpen ? t.nav.closeMenu : t.nav.openMenu}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-navigation"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div
        id="mobile-navigation"
        className={cn(
          "lg:hidden",
          isMenuOpen ? "block" : "hidden",
        )}
      >
        <div className="mx-auto max-w-7xl px-4 pb-4 sm:px-6">
          <nav
            aria-label={t.nav.openMenu}
            className="flex max-h-[calc(100dvh-88px)] flex-col gap-1 overflow-y-auto rounded-lg border border-border/60 bg-card p-2 shadow-[var(--shadow-elevated)] animate-fade-in"
          >
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} className={mobileLink} onClick={() => setIsMenuOpen(false)}>
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              onClick={() => setIsMenuOpen(false)}
              className="rounded-md px-4 py-3 font-semibold text-foreground transition-colors hover:bg-secondary hover:text-highlight"
            >
              {t.nav.contact}
            </Link>

            <div className="mt-1 border-t border-border/60 px-2 pt-3">
              <p className="mb-2 px-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">{t.nav.language}</p>
              <LanguageSwitcher variant="segmented" />
            </div>

            {user ? (
              <div className="mt-2 flex flex-col gap-1 border-t border-border/60 pt-2">
                <Button onClick={handleDashboardNavigation} variant="ghost" className="w-full justify-start text-primary">
                  <User className="mr-2 h-4 w-4" />
                  {roleLabel}
                </Button>
                <Button onClick={signOut} variant="ghost" className="w-full justify-start text-primary">
                  <LogOut className="mr-2 h-4 w-4" />
                  {t.nav.logout}
                </Button>
              </div>
            ) : (
              <Button asChild className="mt-2 h-11 w-full">
                <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                  {t.nav.login}
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </Button>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
};
